// resources/js/ashnet-components/plugins/grid/GridStateManager.js

const CURRENT_PREFIX = 'ashrms-grid-';
const OLD_PREFIX = 'ashrms-';
const CURRENT_GRID_STATE_VERSION = 2;
const RESET_DONE_KEY = 'ashrms-grid-ResetDone';
const RESET_VERSION = '2026-01-06';
const LOG_PREFIX = '[ashrms-grid-state]';

export class GridStateManager {
    /**
     * Static: Global one-time reset – deletes old (ashrms-*) and new (ashrms-grid-*) states
     */
    static performGlobalReset() {
        if (localStorage.getItem(RESET_DONE_KEY) !== RESET_VERSION) {
            let deletedCount = 0;

            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(OLD_PREFIX) || key.startsWith(CURRENT_PREFIX)) {
                    console.log(`${LOG_PREFIX} Deleting outdated state: ${key}`);
                    localStorage.removeItem(key);
                    deletedCount++;
                }
            });

            localStorage.setItem(RESET_DONE_KEY, RESET_VERSION);
            console.warn(`${LOG_PREFIX} Global reset completed: ${deletedCount} state(s) deleted`);
        }
    }

    /**
     * Static: Targeted reset of specific grids (perfect for updates with column changes)
     * @param {string|Array<string>} gridNames e.g. 'users' or ['users', 'projects']
     */
    static resetSpecificGrids(gridNames) {
        if (!Array.isArray(gridNames)) {
            gridNames = [gridNames];
        }

        let deletedCount = 0;
        gridNames.forEach(name => {
            const key = CURRENT_PREFIX + name;
            if (localStorage.getItem(key) !== null) {
                localStorage.removeItem(key);
                console.log(`${LOG_PREFIX} Targeted reset: Deleted state for grid "${name}"`);
                deletedCount++;
            }
        });

        if (deletedCount > 0) {
            console.warn(`${LOG_PREFIX} Targeted reset completed: ${deletedCount} grid state(s) cleared`);
        }
    }

    constructor(gridName) {
        if (!gridName) throw new Error('GridStateManager: gridName is required');
        this.gridName = gridName;
        this.storageKey = CURRENT_PREFIX + gridName;
    }

    getCurrentVersion() {
        return CURRENT_GRID_STATE_VERSION;
    }

    getFullState(gridInstance) {
        return {
            version: this.getCurrentVersion(),
            columns: gridInstance.state.columns
        };
    }

    save(gridInstance) {
        const stateData = this.getFullState(gridInstance);
        localStorage.setItem(this.storageKey, JSON.stringify(stateData));
        console.log(`${LOG_PREFIX} Saved state for grid "${this.gridName}"`);
    }

    load(gridInstance) {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) {
            console.log(`${LOG_PREFIX} No saved state for grid "${this.gridName}"`);
            return false;
        }

        let savedState;
        try {
            savedState = JSON.parse(raw);
        } catch (e) {
            console.warn(`${LOG_PREFIX} Corrupted state for "${this.gridName}" – deleting`);
            this.remove();
            return false;
        }

        if (savedState.version !== this.getCurrentVersion()) {
            console.log(`${LOG_PREFIX} Outdated state for "${this.gridName}" – discarding`);
            this.remove();
            return false;
        }

        if (savedState.columns) {
            gridInstance.applyState({ columns: savedState.columns });
            console.log(`${LOG_PREFIX} Loaded state for grid "${this.gridName}"`);
        }
        return true;
    }

    remove() {
        localStorage.removeItem(this.storageKey);
        console.log(`${LOG_PREFIX} Removed state for grid "${this.gridName}"`);
    }

    /**
     * Interne Reset-Funktion – wird an den Button gebunden
     */
    performReset(gridInstance, defaultGridState) {
        this.remove();
        gridInstance.applyState(defaultGridState);
        console.log(`${LOG_PREFIX} Reset grid "${this.gridName}" to default`);

        // Button deaktivieren
        const clearButton = gridInstance.widgetMap.clearButton;
        if (clearButton) clearButton.disable();
    }

    /**
     * Statische Methode: Komplette Initialisierung + Reset-Button voll übernehmen
     */
    static initForGrid(gridInstance, gridName) {
        if (!gridInstance || !gridName) {
            throw new Error('GridStateManager.initForGrid: gridInstance and gridName are required');
        }

        const manager = new GridStateManager(gridName);

        // 1. Default-State sichern
        const defaultGridState = manager.getFullState(gridInstance);

        // 2. Gespeicherten State laden
        const hasSavedState = manager.load(gridInstance);

        // 3. Clear-Button finden (per ref: 'clearButton')
        const clearButton = gridInstance.widgetMap.clearButton;

        // Initialen Button-Zustand setzen
        if (clearButton) {
            if (hasSavedState) {
                clearButton.enable();
            } else {
                clearButton.disable();
            }

            // onClick direkt hier überschreiben / setzen
            clearButton.onClick = () => {
                manager.performReset(gridInstance, defaultGridState);
            };
        }

        // 4. Listener für zukünftige Änderungen
        gridInstance.columns.on({
            change: _.debounce(() => {
                manager.save(gridInstance);
                if (clearButton) clearButton.enable();
            }, 500)
        });

        console.log(`${LOG_PREFIX} Fully initialized grid "${gridName}" with state management and reset button`);
    }
}
